import { useState, useEffect, useRef } from 'react';
import './ImageCard.css';

const ImageCard = ({ title, description, images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startDragPos, setStartDragPos] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const imageContainerRef = useRef(null);
  const hasMultipleImages = images.length > 1;

  // Проверяем, является ли устройство мобильным
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    resetZoom();
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    resetZoom();
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    resetZoom();
  };

  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const openFullscreen = () => {
    setIsFullscreen(true);
    document.body.style.overflow = 'hidden';
    resetZoom();
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
    resetZoom();
    document.body.style.overflow = 'auto';
  };

  const handleWheel = (e) => {
    if (!isFullscreen || isMobile) return;
    e.preventDefault();
    
    const delta = -Math.sign(e.deltaY);
    const scaleFactor = delta > 0 ? 1.1 : 0.9;
    const newScale = Math.min(Math.max(scale * scaleFactor, 1), 3);

    if (imageContainerRef.current) {
      const containerRect = imageContainerRef.current.getBoundingClientRect();
      
      const mouseX = e.clientX - containerRect.left;
      const mouseY = e.clientY - containerRect.top;
      
      const currentX = position.x;
      const currentY = position.y;
      
      const newX = mouseX - (mouseX - currentX) * (newScale / scale);
      const newY = mouseY - (mouseY - currentY) * (newScale / scale);

      const imgWidth = containerRect.width * newScale;
      const imgHeight = containerRect.height * newScale;
      
      const maxX = Math.max(0, (imgWidth - containerRect.width) / 2);
      const maxY = Math.max(0, (imgHeight - containerRect.height) / 2);

      setScale(newScale);
      setPosition({
        x: Math.max(-maxX, Math.min(maxX, newX)),
        y: Math.max(-maxY, Math.min(maxY, newY))
      });
    }
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0 || scale <= 1 || isMobile) return;
    e.preventDefault();
    setIsDragging(true);
    setStartDragPos({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || scale <= 1 || isMobile) return;
    
    const newX = e.clientX - startDragPos.x;
    const newY = e.clientY - startDragPos.y;
    
    if (imageContainerRef.current) {
      const rect = imageContainerRef.current.getBoundingClientRect();
      const maxX = (rect.width * scale - rect.width) / 2;
      const maxY = (rect.height * scale - rect.height) / 2;
      
      setPosition({
        x: Math.max(-maxX, Math.min(maxX, newX)),
        y: Math.max(-maxY, Math.min(maxY, newY))
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleFullscreenNavigation = (e, direction) => {
    e.stopPropagation();
    if (direction === 'prev') prevSlide();
    if (direction === 'next') nextSlide();
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isFullscreen && !isMobile) {
        if (e.key === 'Escape') {
          closeFullscreen();
        } else if (hasMultipleImages) {
          if (e.key === 'ArrowLeft') {
            prevSlide();
          } else if (e.key === 'ArrowRight') {
            nextSlide();
          }
        }
        if (e.key === '0') {
          resetZoom();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isFullscreen, currentIndex, hasMultipleImages, isDragging, startDragPos, scale, isMobile]);

  return (
    <>
      <div className="image-card">
        <div className="slider-container">
          <div 
            className="slider-track"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {images.map((img, index) => (
              <div 
                key={index} 
                className="slide"
                onClick={openFullscreen}
              >
                <img 
                  src={img} 
                  alt={`${title} ${index + 1}`} 
                  className="slide-image"
                  loading="lazy"
                />
              </div>
            ))}
          </div>

          {hasMultipleImages && (
            <>
              <button className="slider-btn prev-btn" onClick={prevSlide}>&lt;</button>
              <button className="slider-btn next-btn" onClick={nextSlide}>&gt;</button>
              <div className="slider-dots">
                {images.map((_, index) => (
                  <span
                    key={index}
                    className={`dot ${index === currentIndex ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="card-content">
          <h3 className="card-title">{title}</h3>
          <p className="card-description">{description}</p>
        </div>
      </div>

      {isFullscreen && (
        <div 
          className="fullscreen-overlay" 
          onClick={closeFullscreen}
          onWheel={isMobile ? undefined : handleWheel}
        >
          <div className="fullscreen-content">
            {hasMultipleImages && (
              <>
                <button 
                  className="fullscreen-btn fullscreen-prev-btn"
                  onClick={(e) => handleFullscreenNavigation(e, 'prev')}
                >
                  &lt;
                </button>
                
                <button 
                  className="fullscreen-btn fullscreen-next-btn"
                  onClick={(e) => handleFullscreenNavigation(e, 'next')}
                >
                  &gt;
                </button>
                
                <div className="fullscreen-dots">
                  {images.map((_, index) => (
                    <span
                      key={index}
                      className={`fullscreen-dot ${index === currentIndex ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        goToSlide(index);
                      }}
                    />
                  ))}
                </div>
              </>
            )}
            
            <div 
              ref={imageContainerRef}
              className="fullscreen-image-container"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={isMobile ? undefined : handleMouseDown}
              style={{
                cursor: isMobile ? 'default' : (scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default')
              }}
            >
              <img 
                src={images[currentIndex]} 
                alt={`${title} в полном размере`} 
                className="fullscreen-image"
                style={{
                  transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
                  transition: isMobile || isDragging ? 'none' : 'transform 0.15s ease-out',
                  transformOrigin: '0 0'
                }}
              />
            </div>
            
            <button className="close-btn" onClick={closeFullscreen}>
              &times;
            </button>

            {!isMobile && (
              <div className="zoom-controls">
                <button 
                  className="zoom-btn zoom-in"
                  onClick={(e) => {
                    e.stopPropagation();
                    setScale(prev => Math.min(prev + 0.5, 3));
                  }}
                >
                  +
                </button>
                <button 
                  className="zoom-btn zoom-out"
                  onClick={(e) => {
                    e.stopPropagation();
                    setScale(prev => Math.max(prev - 0.5, 1));
                  }}
                >
                  -
                </button>
                <button 
                  className="zoom-btn zoom-reset"
                  onClick={(e) => {
                    e.stopPropagation();
                    resetZoom();
                  }}
                >
                  ↻
                </button>
              </div>
            )}

            {!isMobile && (
              <div className="zoom-hint">
                {scale > 1 ? (
                  <>
                    <span>Колесико мыши - масштаб</span>
                    <span>Зажмите и тяните - перемещение</span>
                    <span>Нажмите 0 для сброса</span>
                  </>
                ) : (
                  'Прокрутите колесико мыши для увеличения'
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ImageCard;